import React, { useState, useEffect } from 'react';
import { ConfigProvider, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import EditableDrawer from '../../../../../components/EditableDrawer';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import type { DspDriverBreakdownItemType } from '../graphql/queries/getDeliveryProgressMonitoringOfSlaList';

export interface BreakdownDrawerProps {
  visible: boolean;
  title: string;
  data: DspDriverBreakdownItemType[];
  loading?: boolean;
  anomalyType: string; // 'noUpdate48To72h' | 'noUpdateOver72h'
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onClose: () => void;
  onCountClick: (anomalyType: string, dspId: number, dspName: string, driverId?: number, driverName?: string, driverIds?: number[]) => void;
}

const BreakdownDrawer: React.FC<BreakdownDrawerProps> = ({
  visible,
  title,
  data,
  loading = false,
  anomalyType,
  total,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onClose,
  onCountClick
}) => {
  const { t } = useTranslation();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (visible) {
      setExpandedRowKeys([]);
    }
  }, [visible]);

  type RowRecord = {
    key: string;
    dspId: number;
    dspName: string;
    driverDisplay: string;
    driverId?: number;
    driverName?: string;
    count: number;
    isDspRow: boolean;
    children?: RowRecord[];
  };

  const columns: ColumnsType<RowRecord> = [
    {
      title: t('wms.delivery.dailyTaskMonitoring.buttons.dsp'),
      dataIndex: 'dspName',
      key: 'dspName',
      width: 240,
      render: (text: string, record) => (record.isDspRow ? text : null)
    },
    {
      title: t('wms.delivery.dailyTaskMonitoring.buttons.driver'),
      dataIndex: 'driverDisplay',
      key: 'driverDisplay',
      width: 240
    },
    {
      title: t('wms.delivery.parcelSlaMonitoring.breakdown.countColumn'),
      dataIndex: 'count',
      key: 'count',
      width: 140,
      render: (count: number, record: RowRecord) => {
        if (count <= 0) return count;
        return (
          <span
            role="button"
            tabIndex={0}
            className="cursor-pointer text-blue-600 hover:underline"
            onClick={() => {
              if (record.isDspRow) {
                const driverIds = record.children?.map((c) => c.driverId).filter((id): id is number => id != null);
                onCountClick(anomalyType, record.dspId, record.dspName, undefined, undefined, driverIds);
              } else {
                onCountClick(anomalyType, record.dspId, record.dspName, record.driverId, record.driverName);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && count > 0) {
                if (record.isDspRow) {
                  const driverIds = record.children?.map((c) => c.driverId).filter((id): id is number => id != null);
                  onCountClick(anomalyType, record.dspId, record.dspName, undefined, undefined, driverIds);
                } else {
                  onCountClick(anomalyType, record.dspId, record.dspName, record.driverId, record.driverName);
                }
              }
            }}
          >
            {count}
          </span>
        );
      }
    }
  ];

  // 顺序以接口为准：getBreakDriverSideBreakdown 已按数量降序返回 DSP 列表及同一 DSP 下司机列表
  const tableData: RowRecord[] = data.map((dsp) => ({
    key: `dsp-${dsp.dspId}`,
    dspId: dsp.dspId,
    dspName: dsp.dspName,
    driverDisplay: t('wms.delivery.parcelSlaMonitoring.breakdown.all'),
    count: dsp.count,
    isDspRow: true,
    children:
      dsp.drivers.length > 0
        ? dsp.drivers.map((dr) => ({
            key: `driver-${dsp.dspId}-${dr.driverId}`,
            dspId: dsp.dspId,
            dspName: dsp.dspName,
            driverDisplay: dr.driverName,
            driverId: dr.driverId,
            driverName: dr.driverName,
            count: dr.count,
            isDspRow: false
          }))
        : undefined
  }));

  return (
    <EditableDrawer
      title={title}
      visible={visible}
      onClose={onClose}
      width={720}
      showDefaultFooter={false}
      zIndex={998}
    >
      <div className="flex flex-col">
        <div
          className="overflow-x-hidden"
          style={{
            height: '60vh',
            minHeight: 120,
            overflowY: 'auto',
            overscrollBehavior: 'contain'
          }}
        >
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="key"
            loading={loading}
            pagination={false}
            size="small"
            bordered={false}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys ? [...keys] : []),
              rowExpandable: (record) => (record.children?.length ?? 0) > 0
            }}
          />
        </div>
        <div className="flex-shrink-0 pt-3">
          <ConfigProvider getPopupContainer={() => document.body}>
            <TablePaginationBar
              total={total}
              current={page}
              pageSize={pageSize}
              onChange={(p) => onPageChange(p)}
              onShowSizeChange={onPageSizeChange ? (_current, size) => onPageSizeChange(size) : undefined}
            />
          </ConfigProvider>
        </div>
      </div>
    </EditableDrawer>
  );
};

export default BreakdownDrawer;
